import { zodResolver } from "@hookform/resolvers/zod";
import { signUp } from "aws-amplify/auth";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  SIGN_UP_ERROR_MESSAGES,
  SIGN_UP_FALLBACK_ERROR_MESSAGES,
  getCognitoErrorMessage,
} from "./error-messages";
import { useWithLoading } from "./use-with-loading";

// Cognito のパスワードポリシー（13文字以上、大文字・小文字・数字・記号を各1文字以上）と一致させる
export const SignUpFormSchema = z.object({
  email: z.string().email({
    message: "メールアドレスを入力してください",
  }),
  password: z
    .string()
    .min(13, { message: "パスワードは13文字以上で入力してください" })
    .regex(/[a-z]/, { message: "パスワードには小文字を含めてください" })
    .regex(/[A-Z]/, { message: "パスワードには大文字を含めてください" })
    .regex(/[0-9]/, { message: "パスワードには数字を含めてください" })
    .regex(/[^A-Za-z0-9]/, {
      message: "パスワードには記号を含めてください",
    })
    // onBlur で無音に trim すると入力と異なるパスワードが登録されてしまうため、
    // 空白を除去せずエラーとして弾く
    .regex(/^\S(?:.*\S)?$/, {
      message: "パスワードの先頭または末尾に空白を含めることはできません",
    }),
});
export type SignUpFormSchemaType = z.infer<typeof SignUpFormSchema>;

type UseSignUpFormProps = {
  defaultValues: SignUpFormSchemaType;
  onMutation?: VoidFunction;
  onSuccess?: (result: { email: string; userId?: string }) => void;
};

export const useSignUpForm = ({
  defaultValues,
  onMutation,
  onSuccess,
}: UseSignUpFormProps) => {
  const form = useForm<SignUpFormSchemaType>({
    resolver: zodResolver(SignUpFormSchema),
    defaultValues,
  });

  const handleSubmit = useWithLoading(async (values: SignUpFormSchemaType) => {
    onMutation?.();

    try {
      const result = await signUp({
        username: values.email,
        password: values.password,
        options: {
          // Cognito の name 属性は required だが、表示名は後続のプロフィール登録画面で
          // 内部 API (/v2/users) に保存する値が正なので、ここではプレースホルダーとして email を入れる
          userAttributes: { email: values.email, name: values.email },
          autoSignIn: true,
        },
      });
      onSuccess?.({ email: values.email, userId: result.userId });
    } catch (e) {
      if (e instanceof Error && e.name === "UsernameExistsException") {
        form.setError("email", {
          message: SIGN_UP_ERROR_MESSAGES.UsernameExistsException,
        });
        console.error("Sign-up failed:", e.name);
      } else if (e instanceof Error) {
        form.setError("root", {
          message: getCognitoErrorMessage(
            SIGN_UP_ERROR_MESSAGES,
            e.name,
            SIGN_UP_FALLBACK_ERROR_MESSAGES.UNKNOWN_ERROR,
          ),
        });
        // メッセージ本文は転送先のログに英語原文が残るため、例外の種別のみ記録する
        console.error("Sign-up failed:", e.name);
      } else {
        form.setError("root", {
          message: SIGN_UP_FALLBACK_ERROR_MESSAGES.NON_ERROR_THROWN,
        });
        console.error("Caught a non-error object:", e);
      }
    }
  });

  return {
    form,
    handleSubmit: form.handleSubmit(handleSubmit),
    isLoading: handleSubmit.isLoading,
  };
};
